import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import ImageUploadField from '../../../components/shared/ImageUploadField';
import { ArrowLeft, Save, Plus } from 'lucide-react';

const UNIDADES = ['Unidade', 'Kg', 'Dúzia', 'Caixa'];
const FERMENTACOES = ['', 'Natural (Levain)', 'Biológica', 'Química'];

export default function ProdutoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isNew = !id || id === 'novo';

  const [form, setForm] = useState({
    sku: '', descricao: '', categoria_id: '', tipo_fermentacao: '',
    tempo_fermentacao: '', unidade_medida: 'Unidade', pedido_minimo: 1, prazo_entrega: '48h',
    imagem_url: '',
    ncm: '', codigo_barras: '', peso_bruto: 0, peso_liquido: 0,
    estoque_minimo: 0, estoque_maximo: 0, cest: '', origem: 0, is_ativo: true,
  });
  const [categorias, setCategorias] = useState([]);
  const [tiposCliente, setTiposCliente] = useState([]);
  const [precos, setPrecos] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newCat, setNewCat] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catsRes, rotasIgnored] = await Promise.all([
          api.get('/categorias'),
          isAdmin ? api.get('/configuracoes/tipos-cliente') : Promise.resolve(null),
        ]);
        setCategorias(catsRes.data);
        if (isAdmin && rotasIgnored) setTiposCliente(rotasIgnored.data);

        if (!isNew) {
          const prodRes = await api.get(`/produtos/${id}`);
          setForm(f => ({ ...f, ...prodRes.data }));
          if (isAdmin) {
            const precosRes = await api.get(`/produtos/${id}/precos`);
            setPrecos(precosRes.data);
          }
        }
      } catch (err) {
        // silencia erros de carregamento
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        const res = await api.post('/produtos', form);
        if (isAdmin && precos.length > 0) {
          await api.put(`/produtos/${res.data.id}/precos`, precos.map(p => ({ tipo_cliente_id: p.tipo_cliente_id, preco: p.preco || 0 })));
        }
      } else {
        await api.put(`/produtos/${id}`, form);
        if (isAdmin && precos.length > 0) {
          await api.put(`/produtos/${id}/precos`, precos.map(p => ({ tipo_cliente_id: p.tipo_cliente_id, preco: parseFloat(p.preco) || 0 })));
        }
      }
      navigate('/admin/produtos');
    } catch (err) {
      setError(err.userMessage || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const addCat = async () => {
    if (!newCat.trim()) return;
    try {
      const res = await api.post(`/categorias?nome=${encodeURIComponent(newCat)}`);
      setCategorias(c => [...c, res.data]);
      set('categoria_id', res.data.id);
      setNewCat('');
    } catch (err) { setError(err.userMessage || 'Erro ao criar categoria'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>;

  const inputCls = "w-full rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-accent-mid focus:border-brand-primary bg-white";

  return (
    <div data-testid="produto-form">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-brand-bg"><ArrowLeft className="w-5 h-5 text-brand-text-muted" /></button>
        <h1 className="dashboard-heading text-xl">{isNew ? 'Novo Produto' : 'Editar Produto'}</h1>
      </div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-dashboard space-y-3">
          <h3 className="font-dashboard font-semibold text-brand-text">Dados do Produto</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-brand-text-muted mb-1 font-body">SKU / Código</label>
              <input className={inputCls} value={form.sku} onChange={e => set('sku', e.target.value)} placeholder="Auto" data-testid="field-sku" />
            </div>
            <div>
              <label className="block text-xs text-brand-text-muted mb-1 font-body">Unidade *</label>
              <select className={inputCls} value={form.unidade_medida} onChange={e => set('unidade_medida', e.target.value)}>
                {UNIDADES.map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">Descrição *</label>
            <input className={inputCls} value={form.descricao} onChange={e => set('descricao', e.target.value)} data-testid="field-descricao" />
          </div>
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">Categoria</label>
            <div className="flex gap-2">
              <select className={inputCls} value={form.categoria_id} onChange={e => set('categoria_id', e.target.value)}>
                <option value="">Selecione...</option>
                {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
              <input className="rounded-lg border border-brand-border px-2 py-1.5 text-sm font-body w-32" placeholder="Nova..." value={newCat} onChange={e => setNewCat(e.target.value)} />
              <button onClick={addCat} className="p-2 rounded-lg btn-primary"><Plus className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-brand-text-muted mb-1 font-body">Tipo de Fermentação</label>
              <select className={inputCls} value={form.tipo_fermentacao} onChange={e => set('tipo_fermentacao', e.target.value)}>
                {FERMENTACOES.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-brand-text-muted mb-1 font-body">Pedido Mínimo</label>
              <input type="number" className={inputCls} value={form.pedido_minimo} onChange={e => set('pedido_minimo', parseInt(e.target.value) || 1)} min={1} />
            </div>
            <div>
              <label className="block text-xs text-brand-text-muted mb-1 font-body">Prazo de Entrega</label>
              <select className={inputCls} value={form.prazo_entrega} onChange={e => set('prazo_entrega', e.target.value)} data-testid="field-prazo-entrega">
                <option value="24h">24 horas</option>
                <option value="48h">48 horas</option>
                <option value="72h">72 horas</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-brand-text-muted mb-2 font-body">Imagem do Produto</label>
            <ImageUploadField
              value={form.imagem_url}
              onChange={url => set('imagem_url', url)}
              placeholder="Adicionar imagem"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_ativo} onChange={e => set('is_ativo', e.target.checked)} className="rounded" />
            <span className="text-sm font-body text-brand-text">Produto Ativo</span>
          </label>
        </div>

        {/* Fiscal - admin only */}
        {isAdmin && (
          <div className="card-dashboard space-y-3">
            <h3 className="font-dashboard font-semibold text-brand-text">Dados Fiscais</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs text-brand-text-muted mb-1 font-body">NCM</label><input className={inputCls} value={form.ncm} onChange={e => set('ncm', e.target.value)} maxLength={8} /></div>
              <div><label className="block text-xs text-brand-text-muted mb-1 font-body">CEST (7d)</label><input className={inputCls} value={form.cest} onChange={e => set('cest', e.target.value)} maxLength={7} /></div>
              <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Código de Barras</label><input className={inputCls} value={form.codigo_barras} onChange={e => set('codigo_barras', e.target.value)} /></div>
              <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Origem (0-8)</label><input type="number" className={inputCls} value={form.origem} onChange={e => set('origem', parseInt(e.target.value) || 0)} min={0} max={8} /></div>
              <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Peso Bruto (Kg)</label><input type="number" step="0.001" className={inputCls} value={form.peso_bruto} onChange={e => set('peso_bruto', parseFloat(e.target.value) || 0)} /></div>
              <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Peso Líquido (Kg)</label><input type="number" step="0.001" className={inputCls} value={form.peso_liquido} onChange={e => set('peso_liquido', parseFloat(e.target.value) || 0)} /></div>
              <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Estoque Mínimo</label><input type="number" className={inputCls} value={form.estoque_minimo} onChange={e => set('estoque_minimo', parseInt(e.target.value) || 0)} /></div>
              <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Estoque Máximo</label><input type="number" className={inputCls} value={form.estoque_maximo} onChange={e => set('estoque_maximo', parseInt(e.target.value) || 0)} /></div>
            </div>
          </div>
        )}

        {/* Price tables - admin only */}
        {isAdmin && tiposCliente.length > 0 && (
          <div className="card-dashboard md:col-span-2">
            <h3 className="font-dashboard font-semibold text-brand-text mb-3">Tabelas de Preço por Tipo de Cliente</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {tiposCliente.map(tipo => {
                const preco = precos.find(p => p.tipo_cliente_id === tipo.id);
                return (
                  <div key={tipo.id}>
                    <label className="block text-xs text-brand-text-muted mb-1 font-body">{tipo.nome}</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text-muted text-sm font-body">R$</span>
                      <input
                        type="number"
                        step="0.01"
                        className={`${inputCls} pl-9`}
                        value={preco?.preco || ''}
                        onChange={e => {
                          const val = e.target.value;
                          if (preco) {
                            setPrecos(ps => ps.map(p => p.tipo_cliente_id === tipo.id ? { ...p, preco: val } : p));
                          } else {
                            setPrecos(ps => [...ps, { tipo_cliente_id: tipo.id, preco: val }]);
                          }
                        }}
                        placeholder="0,00"
                        data-testid={`preco-${tipo.nome}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3 mt-6">
        <button onClick={() => navigate(-1)} className="px-5 py-2.5 rounded-lg border border-brand-border font-body text-sm">Cancelar</button>
        <button onClick={handleSave} disabled={saving} className="px-6 py-2.5 rounded-lg btn-primary font-dashboard font-semibold text-sm flex items-center gap-2" data-testid="save-produto-btn">
          <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}
